
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Typings
import { IProvider } from "@/models/providers";
import {
  IAffiliateTierConditionProviderData,
  IAffiliateTierConditionProviderMethods
} from "@/models/providers/affiliateTierConditions";
import {
  IAffiliateTier,
  IAffiliateTierCondition,
  IAffiliateTierConditionForm
} from "@/models/affiliate";
import { IState } from "@/store";
import { IProduct } from "@/models/products";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      affiliateTierConditionScope(state: IState, getters) {
        return getters[`data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/scope`](
          this.affiliateTierId,
          this.affiliateTierConditionId
        );
      },
      affiliateTierCondition(state: IState, getters) {
        return (
          getters["data/single"]({
            storeModule: DataModules.AFFILIATES_TIERS_CONDITIONS,
            scope: this.affiliateTierConditionScope
          }) || {}
        );
      }
    })
  }
})
export default class AffiliateTierConditionProvider
  extends Mixins(RequestPromises, BillingCyclesStore)
  implements IProvider {
  // State

  public affiliateTierConditionScope!: string;
  public affiliateTierCondition!: IAffiliateTierCondition;

  // Props

  @Prop({ required: true })
  public affiliateTierId!: IAffiliateTier["id"];

  @Prop({ required: true })
  public affiliateTierConditionId!: IAffiliateTierCondition["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    await this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([
        this.getBillingCycles(),
        this.getAffiliateTierCondition(this.isReloading)
      ]);
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  // Methods

  public getAffiliateTierCondition(ignoreStored) {
    if (this.isNewTier || this.isNewCondition) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/get`,
      {
        tierId: this.affiliateTierId,
        conditionId: this.affiliateTierConditionId,
        scope: this.affiliateTierConditionScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public createTierCondition(data: IAffiliateTierConditionForm) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/create`,
      {
        id: this.affiliateTierId,
        data
      }
    );
  }

  public updateTierCondition(data: IAffiliateTierConditionForm) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/update`,
      {
        tierId: this.affiliateTierId,
        id: this.affiliateTierConditionId,
        data
      }
    );
  }

  public attachSelectedProducts(
    productIds: IProduct["id"][],
    conditionId = null
  ) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/attachSelectedProducts`,
      {
        tierId: this.affiliateTierId,
        conditionId: _.isNil(conditionId)
          ? this.affiliateTierConditionId
          : conditionId,
        data: {
          product_ids: productIds
        }
      }
    );
  }

  public detachSelectedProducts(productIds: IProduct["id"][]) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/detachSelectedProducts`,
      {
        tierId: this.affiliateTierId,
        conditionId: this.affiliateTierConditionId,
        data: {
          product_ids: productIds
        }
      }
    );
  }

  public reorderConditions(conditionIds: IAffiliateTierCondition["id"][]) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_TIERS_CONDITIONS}/reorderTierConditions`,
      {
        tierId: this.affiliateTierId,
        data: {
          affiliate_tier_condition_ids: conditionIds
        }
      }
    );
  }

  // Computed

  public get isNewCondition() {
    return _.isNil(this.affiliateTierConditionId);
  }

  public get isNewTier() {
    return _.isNil(this.affiliateTierId);
  }

  public get isDisabled() {
    return this.isNewCondition ? !this.canCreate : !this.canUpdate;
  }

  public get canCreate() {
    return this.$userCan("create_affiliate_tier_condition");
  }

  public get canUpdate() {
    return this.$userCan("update_affiliate_tier_condition");
  }

  public get canDelete() {
    return this.$userCan("delete_affiliate_tier_condition");
  }

  // Guarding

  public guardFunctionalities = ["get_affiliate_tier_condition"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("tierConditionsProviderData")
  public providerData(): IAffiliateTierConditionProviderData {
    return {
      isDisabled: this.isDisabled,
      canDelete: this.canDelete,
      affiliateTierCondition: this.affiliateTierCondition,
      billingTermsOptions: this.billingTermsOptions,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading
    };
  }

  @Provide("tierConditionsProviderMethods")
  public get providerMethods(): IAffiliateTierConditionProviderMethods {
    return {
      createTierCondition: this.createTierCondition,
      updateTierCondition: this.updateTierCondition,
      attachSelectedProducts: this.attachSelectedProducts,
      detachSelectedProducts: this.detachSelectedProducts,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affTierConditionData: this.providerData(),
        $affTierConditionMethods: this.providerMethods
      })
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.AFFILIATES_TIERS_CONDITIONS,
      scope: this.affiliateTierConditionScope,
      vm: this
    });
  }
}
